import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import odooApi from '../services/odooApi';

const CurrencyContext = createContext();
const PREFERRED_CURRENCY_EVENT = 'velaris:preferred-currency';
const CURRENCY_SYMBOL_OVERRIDES = {
  AED: 'AED',
};

const FALLBACK_CURRENCIES = [
  { code: 'AED', name: 'UAE Dirham', symbol: 'AED', countryCode: 'AE', rateFromBase: 1 },
  { code: 'USD', name: 'US Dollar', symbol: '$', countryCode: 'US', rateFromBase: 0.272 },
  { code: 'EUR', name: 'Euro', symbol: '€', countryCode: 'EU', rateFromBase: 0.251 },
];

const countryCodeToFlag = (countryCode) => {
  const normalized = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) {
    return '🌐';
  }

  return String.fromCodePoint(
    ...[...normalized].map((char) => 127397 + char.charCodeAt(0)),
  );
};

const getStoredCurrency = () => {
  const directValue = localStorage.getItem('velaris_currency');
  if (directValue) {
    return directValue.toUpperCase();
  }

  try {
    const storedUser = JSON.parse(localStorage.getItem('velaris_user') || '{}');
    return String(storedUser.preferredCurrency || 'AED').toUpperCase();
  } catch {
    return 'AED';
  }
};

const normalizeCurrencyCode = (currencyCode, currencies, fallbackCode) => {
  const normalizedCode = String(currencyCode || '').trim().toUpperCase();
  const resolvedFallback = String(fallbackCode || 'AED').trim().toUpperCase();
  const supportedCodes = new Set(currencies.map((currency) => currency.code));

  if (normalizedCode && supportedCodes.has(normalizedCode)) {
    return normalizedCode;
  }
  if (supportedCodes.has(resolvedFallback)) {
    return resolvedFallback;
  }
  return currencies[0]?.code || 'AED';
};

const resolveCurrencySymbol = (currencyCode, currencySymbol) => {
  const normalizedCode = String(currencyCode || '').trim().toUpperCase();
  const override = CURRENCY_SYMBOL_OVERRIDES[normalizedCode];
  if (override) {
    return override;
  }

  const normalizedSymbol = String(currencySymbol || '').trim();
  return normalizedSymbol || normalizedCode;
};

const fallbackCurrencyOptions = FALLBACK_CURRENCIES.map((currency) => ({
  ...currency,
  flag: countryCodeToFlag(currency.countryCode),
}));

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState(() => fallbackCurrencyOptions);
  const [baseCurrency, setBaseCurrency] = useState('AED');
  const [currentCurrency, setCurrentCurrency] = useState(() => getStoredCurrency());

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      const data = await odooApi.getCurrencies();
      if (!isMounted) {
        return;
      }

      const nextCurrencies = (data.currencies || [])
        .map((currency) => {
          const code = String(currency.code || '').trim().toUpperCase();
          return {
            code,
            name: String(currency.name || '').trim(),
            symbol: resolveCurrencySymbol(code, currency.symbol || currency.code),
            countryCode: String(currency.countryCode || '').trim().toUpperCase(),
            rateFromBase: Number(currency.rateFromBase ?? 1),
          };
        })
        .filter((currency) => currency.code)
        .map((currency) => ({
          ...currency,
          flag: countryCodeToFlag(currency.countryCode),
        }));

      const resolvedCurrencies = nextCurrencies.length
        ? nextCurrencies
        : fallbackCurrencyOptions;
      const resolvedBaseCurrency = normalizeCurrencyCode(
        data.baseCurrency,
        resolvedCurrencies,
        'AED',
      );

      setCurrencies(resolvedCurrencies);
      setBaseCurrency(resolvedBaseCurrency);
      setCurrentCurrency((previousCurrency) => {
        const nextCurrency = normalizeCurrencyCode(
          previousCurrency,
          resolvedCurrencies,
          resolvedBaseCurrency,
        );
        localStorage.setItem('velaris_currency', nextCurrency);
        odooApi.setCurrency(nextCurrency);
        return nextCurrency;
      });
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('velaris_currency', currentCurrency);
    odooApi.setCurrency(currentCurrency);
  }, [currentCurrency]);

  useEffect(() => {
    const handlePreferredCurrency = (event) => {
      const preferredCurrency = event?.detail;
      if (!preferredCurrency) {
        return;
      }

      setCurrentCurrency((previousCurrency) => normalizeCurrencyCode(
        preferredCurrency,
        currencies,
        previousCurrency || baseCurrency,
      ));
    };

    window.addEventListener(PREFERRED_CURRENCY_EVENT, handlePreferredCurrency);
    return () => {
      window.removeEventListener(PREFERRED_CURRENCY_EVENT, handlePreferredCurrency);
    };
  }, [baseCurrency, currencies]);

  const rateFor = useCallback((currencyCode) => {
    const normalizedCurrency = normalizeCurrencyCode(
      currencyCode,
      currencies,
      baseCurrency,
    );
    return currencies.find((currency) => currency.code === normalizedCurrency)?.rateFromBase || 1;
  }, [baseCurrency, currencies]);

  const changeCurrency = useCallback(async (currencyCode) => {
    const normalizedCurrency = normalizeCurrencyCode(
      currencyCode,
      currencies,
      baseCurrency,
    );
    setCurrentCurrency(normalizedCurrency);

    try {
      await odooApi.updateProfile({ preferredCurrency: normalizedCurrency });
    } catch {
      // Ignore guest/unauthorized profile sync failures.
    }
  }, [baseCurrency, currencies]);

  const convertPrice = useCallback(
    (amount, sourceCurrency = baseCurrency, targetCurrency = currentCurrency) => {
      const numericAmount = Number(amount ?? 0);
      if (!numericAmount) {
        return 0;
      }

      const normalizedSource = normalizeCurrencyCode(
        sourceCurrency,
        currencies,
        baseCurrency,
      );
      const normalizedTarget = normalizeCurrencyCode(
        targetCurrency,
        currencies,
        currentCurrency,
      );

      if (normalizedSource === normalizedTarget) {
        return numericAmount;
      }

      const amountInBase = normalizedSource === baseCurrency
        ? numericAmount
        : numericAmount / rateFor(normalizedSource);

      if (normalizedTarget === baseCurrency) {
        return amountInBase;
      }

      return amountInBase * rateFor(normalizedTarget);
    },
    [baseCurrency, currencies, currentCurrency, rateFor],
  );

  const formatPrice = useCallback(
    (amount, sourceCurrency = baseCurrency, targetCurrency = currentCurrency) => {
      const normalizedTarget = normalizeCurrencyCode(
        targetCurrency,
        currencies,
        currentCurrency,
      );
      const symbol = resolveCurrencySymbol(
        normalizedTarget,
        currencies.find((currency) => currency.code === normalizedTarget)?.symbol,
      );
      const converted = Math.round(
        convertPrice(amount, sourceCurrency, normalizedTarget),
      );

      return `${symbol} ${converted.toLocaleString()}`;
    },
    [baseCurrency, convertPrice, currencies, currentCurrency],
  );

  const formatCurrentAmount = useCallback(
    (amount) => formatPrice(amount, currentCurrency, currentCurrency),
    [currentCurrency, formatPrice],
  );

  const currentCurrencyMeta = currencies.find((currency) => currency.code === currentCurrency)
    || currencies[0];

  const value = {
    baseCurrency,
    currentCurrency,
    currencies,
    changeCurrency,
    convertPrice,
    formatPrice,
    formatCurrentAmount,
    symbol: currentCurrencyMeta?.symbol || currentCurrency,
    flag: currentCurrencyMeta?.flag || '🌐',
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
